import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, ChevronRight, Filter } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const SEVERITY_STYLES = {
    high:   { bar: "bg-red-500",    tag: "text-red-700 bg-red-50 border-red-200",       label: "High" },
    medium: { bar: "bg-amber-500",  tag: "text-amber-700 bg-amber-50 border-amber-200", label: "Medium" },
    low:    { bar: "bg-stone-400",  tag: "text-stone-700 bg-stone-100 border-stone-200",label: "Low" },
};

const KIND_ICONS = {
    card_lost_blocked:      "🔒",
    missing_statement:      "📄",
    overdue_bill:           "⚠️",
    uncategorized_expenses: "🏷️",
    open_dispute:           "⚖️",
    unposted_payment:       "🕒",
    card_expiring:          "⏳",
};

export default function ExceptionCentre() {
    const { hasRole } = useAuth();
    const [data, setData] = useState(null);
    const [includeResolved, setIncludeResolved] = useState(false);
    const [severity, setSeverity] = useState("all");

    const load = () => {
        api.get("/exceptions", { params: { include_resolved: includeResolved } })
            .then((r) => setData(r.data))
            .catch(() => setData({ count: 0, items: [], by_severity: {} }));
    };
    useEffect(() => { load(); }, [includeResolved]);

    const resolve = async (key) => {
        try {
            await api.post(`/exceptions/${key}/resolve`);
            toast.success("Marked resolved");
            load();
        } catch (e) {
            toast.error("Failed to resolve");
        }
    };
    const unresolve = async (key) => {
        try {
            await api.delete(`/exceptions/${key}/resolve`);
            toast.success("Re-opened");
            load();
        } catch (e) {
            toast.error("Failed to reopen");
        }
    };

    const filtered = (data?.items || []).filter((i) => severity === "all" || i.severity === severity);

    return (
        <div data-testid="exception-centre-page">
            <div className="mb-6">
                <div className="eyebrow">Control Centre · Everything unresolved</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Exception Centre.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    One place for every unresolved item across the dashboard — missing statements,
                    overdue bills, open disputes, unposted payments, and cards nearing expiry.
                </p>
            </div>

            {/* Summary strip */}
            {data && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {["high", "medium", "low"].map((sev) => {
                        const style = SEVERITY_STYLES[sev];
                        return (
                            <div key={sev} className="kpi-card cursor-pointer" onClick={() => setSeverity(severity === sev ? "all" : sev)}>
                                <div className="eyebrow flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${style.bar}`} />
                                    {style.label} severity
                                </div>
                                <div className="font-display text-3xl mt-2 tabular-nums">{data.by_severity?.[sev] ?? 0}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="text-xs text-stone-500 uppercase tracking-widest flex items-center gap-1.5">
                    <Filter size={11} /> Filter
                </div>
                {["all", "high", "medium", "low"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setSeverity(s)}
                        data-testid={`exc-filter-${s}`}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors capitalize ${severity === s ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400 text-stone-700"}`}
                    >
                        {s}
                    </button>
                ))}
                <label className="ml-auto flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={includeResolved}
                        onChange={(e) => setIncludeResolved(e.target.checked)}
                        data-testid="exc-include-resolved"
                    />
                    Show resolved history
                </label>
            </div>

            {/* Items */}
            {!data ? (
                <div className="text-center text-stone-500 py-14">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-stone-200 rounded-lg bg-white text-center py-16">
                    <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-3" />
                    <div className="font-display text-xl text-stone-900">All clear.</div>
                    <div className="text-sm text-stone-500 mt-1">No unresolved items match your filter.</div>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((item) => {
                        const style = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.low;
                        return (
                            <div
                                key={item.key}
                                data-testid={`exc-item-${item.key}`}
                                className={`bg-white border border-stone-200 rounded-lg overflow-hidden flex items-stretch ${item.resolved ? "opacity-60" : ""}`}
                            >
                                <div className={`w-1 ${style.bar}`} />
                                <div className="flex-1 p-4 flex items-start gap-4 flex-wrap">
                                    <div className="text-2xl mt-0.5 select-none">{KIND_ICONS[item.kind] || "❗"}</div>
                                    <div className="flex-1 min-w-[220px]">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`inline-flex items-center text-[10px] font-medium uppercase tracking-widest px-1.5 py-0.5 rounded border ${style.tag}`}>
                                                {style.label}
                                            </span>
                                            <span className="text-[10px] text-stone-400 uppercase tracking-widest">{item.kind.replace(/_/g, " ")}</span>
                                            {item.resolved && (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                                                    <CheckCircle2 size={11} /> Resolved
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-medium text-stone-900 mt-1">{item.title}</div>
                                        <div className="text-sm text-stone-500 mt-0.5">{item.detail}</div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {item.primary_action && (
                                            <Link
                                                to={item.primary_action.path}
                                                className="inline-flex items-center gap-1 text-xs text-stone-900 hover:underline"
                                                data-testid={`exc-action-${item.key}`}
                                            >
                                                {item.primary_action.label} <ChevronRight size={13} />
                                            </Link>
                                        )}
                                        {hasRole("admin", "finance") && (
                                            item.resolved ? (
                                                <Button size="sm" variant="outline" onClick={() => unresolve(item.key)} data-testid={`exc-reopen-${item.key}`}>
                                                    Re-open
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="outline" onClick={() => resolve(item.key)} data-testid={`exc-resolve-${item.key}`}>
                                                    <CheckCircle2 size={13} className="mr-1" /> Resolve
                                                </Button>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
